/**
 * Client ของเคส NCR (`/v1/ncr-cases`, D-108) — ตามห้าเส้นทางที่ router จริงของ
 * เซิร์ฟเวอร์ serve (`modules/quality/router.py`), ไม่ใช่แค่ตาม
 * `docs/API_CONTRACT.md`
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ไม่มีเคส"
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว — header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CloseNcrCaseBody,
  CreateNcrCaseBody,
  InvestigateNcrCaseBody,
  NcrCase,
  NcrCaseListPage,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const NCR_CASES_PATH = '/v1/ncr-cases';

export function ncrCasePath(ncrId: string): string {
  return `${NCR_CASES_PATH}/${encodeURIComponent(ncrId)}`;
}

export function ncrCaseInvestigatePath(ncrId: string): string {
  return `${ncrCasePath(ncrId)}/investigate`;
}

export function ncrCaseClosePath(ncrId: string): string {
  return `${ncrCasePath(ncrId)}/close`;
}

/**
 * พารามิเตอร์ของ `GET /v1/ncr-cases` ที่ router จริงรับ — ทั้งสี่แคบลงได้เท่านั้น
 * ไม่เคยกว้างขึ้น: เซิร์ฟเวอร์กรองตาม `plant_scope` ของผู้เรียกก่อนเสมอ และ
 * `plant_code` นอกขอบเขตตอบ `403` ไม่ใช่หน้าว่าง
 */
export interface NcrCaseListQuery {
  readonly plant_code?: string;
  readonly status?: string;
  readonly reference_doc_type?: string;
  readonly reference_doc_id?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export function buildNcrCaseListQuery(query: NcrCaseListQuery = {}): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  const referenceDocType = query.reference_doc_type?.trim() ?? '';
  if (referenceDocType !== '') {
    params.append('reference_doc_type', referenceDocType);
  }
  const referenceDocId = query.reference_doc_id?.trim() ?? '';
  if (referenceDocId !== '') {
    params.append('reference_doc_id', referenceDocId);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจโดยไม่เพิ่ม top-level
   * error code ใหม่ */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ router นี้ตอบจริง แยกเป็นสถานะของตัวเอง
 *
 * `403` ที่นี่แปลว่า **โรงงานที่ระบุ (หรือโรงงานของเคส) อยู่นอกขอบเขตของผู้เรียก**
 * เสมอ ทั้งการเปิดเคส การสอบสวน และการปิดเคสล้วนเป็นการเขียนเข้าโรงงานหนึ่ง (D-004)
 *
 * `404` ที่นี่คือ **การอ่าน** — เคสในโรงงานนอกขอบเขตตอบ `404` เหมือนไม่มีเคสนั้นอยู่
 * เลย ต่างจาก `403` ของสองการเปลี่ยนสถานะโดยตั้งใจ (`router.py` ระบุไว้ตรง ๆ ว่า
 * consequence ที่ยอมรับคือคนที่ถือ id อยู่แล้วรู้ว่าเคสมีอยู่ที่อื่น)
 */
export type NcrCaseFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type NcrCaseListResult =
  | { readonly status: 'ready'; readonly page: NcrCaseListPage }
  | NcrCaseFailure;

export type NcrCaseResult =
  | { readonly status: 'ready'; readonly ncrCase: NcrCase }
  | NcrCaseFailure;

export interface NcrCaseRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** `undefined` แปลว่า "ผิดรูป" — `null` (ตามที่ Pydantic `Optional[...] = None`
 * serialize ออกมา) เป็นค่าที่ถูกต้อง */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * เคสหนึ่งใบ — `disposition`/`decided_by_employee_id`/`decided_at` เป็นสามฟิลด์
 * เดียวที่ nullable กันเป็นชุด (`ck_ncr_case_disposition_record_is_whole` ฝั่ง
 * เซิร์ฟเวอร์) แต่ parser นี้ไม่ตรวจว่าทั้งสามมาครบเป็นชุดหรือไม่ — ปล่อยให้เป็น
 * ข้อเท็จจริงของ payload ที่ได้รับมา ไม่ใช่กติกาที่หน้าจอฝั่งนี้ต้องคอยพิสูจน์ซ้ำ
 */
export function parseNcrCase(raw: unknown): NcrCase | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const ncrId = requiredString(record.ncr_id);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const referenceDocType = requiredString(record.reference_doc_type);
  const referenceDocId = requiredString(record.reference_doc_id);
  const status = requiredString(record.status);
  const defectDescription = requiredString(record.defect_description);
  const raisedByEmployeeId = requiredString(record.raised_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    ncrId === null ||
    plantId === null ||
    plantCode === null ||
    referenceDocType === null ||
    referenceDocId === null ||
    status === null ||
    defectDescription === null ||
    raisedByEmployeeId === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const investigationNote = nullableString(record.investigation_note);
  const disposition = nullableString(record.disposition);
  const decidedByEmployeeId = nullableString(record.decided_by_employee_id);
  const decidedAt = nullableString(record.decided_at);

  if (
    investigationNote === undefined ||
    disposition === undefined ||
    decidedByEmployeeId === undefined ||
    decidedAt === undefined
  ) {
    return null;
  }

  return {
    ncr_id: ncrId,
    plant_id: plantId,
    plant_code: plantCode,
    reference_doc_type: referenceDocType,
    reference_doc_id: referenceDocId,
    status,
    defect_description: defectDescription,
    raised_by_employee_id: raisedByEmployeeId,
    investigation_note: investigationNote,
    disposition,
    decided_by_employee_id: decidedByEmployeeId,
    decided_at: decidedAt,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseNcrCaseListPage(raw: unknown): NcrCaseListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: NcrCase[] = [];
  for (const entry of record.items) {
    const parsed = parseNcrCase(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/** เหลือเฉพาะคีย์ที่ contract รับ — เขียนทีละตัวแทนการ spread เพื่อไม่ให้ฟิลด์ที่
 * เซิร์ฟเวอร์เป็นเจ้าของ (`ncr_id`, `status`, `disposition`, ...) หลุดไปกับ state
 * ฟอร์ม */
export function serializeCreateNcrCaseBody(body: CreateNcrCaseBody): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    reference_doc_type: body.reference_doc_type,
    reference_doc_id: body.reference_doc_id,
    defect_description: body.defect_description,
    ...(body.investigation_note === undefined
      ? {}
      : { investigation_note: body.investigation_note }),
  };
}

export function serializeInvestigateNcrCaseBody(
  body: InvestigateNcrCaseBody,
): Record<string, unknown> {
  return { investigation_note: body.investigation_note };
}

export function serializeCloseNcrCaseBody(body: CloseNcrCaseBody): Record<string, unknown> {
  return {
    disposition: body.disposition,
    ...(body.investigation_note === undefined
      ? {}
      : { investigation_note: body.investigation_note }),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | NcrCaseFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: NcrCaseRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

function toCaseResult(result: PayloadResult): NcrCaseResult {
  if (result.status !== 'payload') {
    return result;
  }
  const ncrCase = parseNcrCase(result.payload);
  return ncrCase === null ? { status: 'invalid-response' } : { status: 'ready', ncrCase };
}

/** `GET /v1/ncr-cases` — กรองตามขอบเขตโรงงานของผู้เรียกเสมอ */
export async function fetchNcrCases(
  query: NcrCaseListQuery = {},
  options: NcrCaseRequestOptions = {},
): Promise<NcrCaseListResult> {
  const result = await requestPayload(
    `${NCR_CASES_PATH}${buildNcrCaseListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseNcrCaseListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/ncr-cases` — เปิดเคสใหม่ที่ `OPEN` ตอบ `201`
 *
 * **ไม่มีล็อตใดเคลื่อนที่นี่** — การเปิดเคสคือการรายงานปัญหา ไม่ใช่การตัดสินใจ
 * (D-108 ข้อ 2); สถานะของล็อตเปลี่ยนที่ `/close` เท่านั้น
 */
export async function createNcrCase(
  body: CreateNcrCaseBody,
  options: NcrCaseRequestOptions = {},
): Promise<NcrCaseResult> {
  return toCaseResult(
    await requestPayload(
      NCR_CASES_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreateNcrCaseBody(body)),
      },
      options,
    ),
  );
}

/** `POST /v1/ncr-cases/{ncr_id}/investigate` — `OPEN` → `UNDER_REVIEW` */
export async function investigateNcrCase(
  ncrId: string,
  body: InvestigateNcrCaseBody,
  options: NcrCaseRequestOptions = {},
): Promise<NcrCaseResult> {
  return toCaseResult(
    await requestPayload(
      ncrCaseInvestigatePath(ncrId),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeInvestigateNcrCaseBody(body)),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/ncr-cases/{ncr_id}/close` — **THE HEADLINE.** บันทึกการตัดสินใจ และ
 * ย้ายสถานะคุณภาพของล็อตเมื่อเคสนี้อ้างถึง `LOT` (D-108 ข้อ 2) — เส้นทางเดียวใน
 * ทั้งเวอร์ติคัลนี้ที่ทำอย่างนั้น
 */
export async function closeNcrCase(
  ncrId: string,
  body: CloseNcrCaseBody,
  options: NcrCaseRequestOptions = {},
): Promise<NcrCaseResult> {
  return toCaseResult(
    await requestPayload(
      ncrCaseClosePath(ncrId),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCloseNcrCaseBody(body)),
      },
      options,
    ),
  );
}

/** อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี */
export function ruleFromFailure(failure: NcrCaseFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}
